import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../../../../../redux/store';
import { API_BASE_URL } from '../../../../../helpers/common';

const apiV1BaseUrl = `${String(API_BASE_URL || '').replace(/\/$/, '')}/v1`;

export interface EOfficeNotification {
  _id?: string;
  title?: string;
  message?: string;
  type?: string;
  isRead?: boolean;
  createdAt?: string;
  [key: string]: unknown;
}

export interface EOfficeNotificationCreateRequest {
  title: string;
  message: string;
  type?: string;
  userIds?: string[];
  [key: string]: unknown;
}

/**
 * eOffice-style notification HTTP API (v1), co-located with the Notifications page.
 * Uses the same paths as eOffice.admin `notification.api.ts`.
 */
export const notificationPageApi = createApi({
  reducerPath: 'notificationPageApi',
  baseQuery: fetchBaseQuery({
    baseUrl: apiV1BaseUrl,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).user.info.accessToken;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Notification'],
  endpoints: build => ({
    getNotifications: build.query<
      { data: EOfficeNotification[]; total: number },
      { page?: number; limit?: number; isRead?: boolean }
    >({
      query: params => {
        const query = new URLSearchParams();
        if (params.page) query.append('page', params.page.toString());
        if (params.limit) query.append('limit', params.limit.toString());
        if (params.isRead !== undefined) {
          query.append('isRead', params.isRead.toString());
        }
        return {
          url: `notification/list?${query.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Notification'],
    }),

    getNotificationCount: build.query<{ unread: number }, void>({
      query: () => ({
        url: 'notification/list',
        method: 'GET',
      }),
      providesTags: ['Notification'],
    }),

    createNotification: build.mutation<
      EOfficeNotification,
      EOfficeNotificationCreateRequest
    >({
      query: data => ({
        url: 'notification',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Notification'],
    }),

    markAsRead: build.mutation<void, string>({
      query: id => ({
        url: `notification/${id}/read`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Notification'],
    }),

    markAllAsRead: build.mutation<void, void>({
      query: () => ({
        url: 'notification/read-all',
        method: 'PATCH',
      }),
      invalidatesTags: ['Notification'],
    }),

    deleteNotification: build.mutation<void, string>({
      query: id => ({
        url: `notification/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Notification'],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useGetNotificationCountQuery,
  useCreateNotificationMutation,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
  useDeleteNotificationMutation,
} = notificationPageApi;
